"use client";

import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Upload, Trash2, Edit } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ViewJobDescriptionDialog } from "./ViewJobDescriptionDialog";
import { ProvideJobDescriptionDialog } from "./ProvideJobDescriptionDialog";
import { EditJobDescriptionDialog } from "./EditJobDescriptionDialog";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogAction, AlertDialogCancel, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

interface RoleJD {
  id: string;
  role_title: string;
  role_description: {
    text: string;
    pdf_url?: string;
    pdf_filename?: string;
  };
}

interface ProjectRequirements {
  directors_count?: number;
  managers_count?: number;
  team_members_count?: number;
  organizational_structure?: string;
}

interface RoleJDTableProps {
  onRoleSelect?: (roleId: string, index: number) => void;
  key?: number;
  className?: string;
}

export function RoleJDTable({
  onRoleSelect,
  key,
  className
}: RoleJDTableProps) {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [roles, setRoles] = useState<RoleJD[]>([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [projectRequirements, setProjectRequirements] = useState<ProjectRequirements>({});
  const [projectName, setProjectName] = useState("");
  const [viewJDDialogOpen, setViewJDDialogOpen] = useState(false);
  const [provideJDDialogOpen, setProvideJDDialogOpen] = useState(false);
  const [editJDDialogOpen, setEditJDDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedRoleTitle, setSelectedRoleTitle] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string>("");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const sortRoles = (rolesArray: RoleJD[]) => {
    return [...rolesArray].sort((a, b) => {
      const roleA = a.role_title.toLowerCase();
      const roleB = b.role_title.toLowerCase();
      if (roleA.includes('director') && !roleB.includes('director')) return -1;
      if (!roleA.includes('director') && roleB.includes('director')) return 1;
      if (roleA.includes('manager') && !roleB.includes('manager')) return -1;
      if (!roleA.includes('manager') && roleB.includes('manager')) return 1;
      return roleA.localeCompare(roleB);
    });
  };

  const fetchRoles = async () => {
    if (!projectId) return;
    try {
      const rolesResponse = await supabase
        .from('role_jd')
        .select('id, role_title, role_description')
        .eq('project_id', projectId);

      if (rolesResponse.error) throw rolesResponse.error;

      if (rolesResponse.data) {
        const transformedRoles: RoleJD[] = rolesResponse.data.map(role => ({
          id: role.id,
          role_title: role.role_title,
          role_description: typeof role.role_description === 'string' ? {
            text: role.role_description
          } : typeof role.role_description === 'object' && role.role_description !== null ? {
            text: (role.role_description as {
              text?: string;
            }).text || '',
            pdf_url: (role.role_description as {
              pdf_url?: string;
            }).pdf_url,
            pdf_filename: (role.role_description as {
              pdf_filename?: string;
            }).pdf_filename
          } : {
            text: ''
          }
        }));
        const sortedRoles = sortRoles(transformedRoles);
        setRoles(sortedRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      try {
        const [requirementsResponse, projectResponse] = await Promise.all([
          supabase.from('project_requirements').select('directors_count, managers_count, team_members_count, organizational_structure').eq('project_id', projectId).maybeSingle(),
          supabase.from('project_requirements').select('name').eq('project_id', projectId).maybeSingle()
        ]);

        if (requirementsResponse.error) throw requirementsResponse.error;
        if (projectResponse.error) throw projectResponse.error;

        if (requirementsResponse.data) {
          setProjectRequirements(requirementsResponse.data);
        }
        if (projectResponse.data) {
          setProjectName(projectResponse.data.name);
        }

        await fetchRoles();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [projectId, onRoleSelect, key]);

  const handleRowClick = async (index: number, roleId: string) => {
    if (isLoading) return;

    setIsLoading(true);
    setSelectedRowIndex(index);
    try {
      if (onRoleSelect) {
        await onRoleSelect(roleId, index);
      }
    } catch (error) {
      console.error('Error in handleRowClick:', error);
    }
  };

  const handleViewJDClick = (roleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedRoleId(roleId);
    setViewJDDialogOpen(true);
  };

  const handleProvideJDClick = (roleId: string, roleTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedRoleId(roleId);
    setSelectedRoleTitle(roleTitle);
    setProvideJDDialogOpen(true);
  };

  const handleDeleteJDClick = (roleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setRoleToDelete(roleId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      const { error } = await supabase
        .from('role_jd')
        .update({
          role_description: {}
        })
        .eq('id', roleToDelete);

      if (error) throw error;

      await fetchRoles();
      setDeleteConfirmOpen(false);
      setRoleToDelete("");
    } catch (error) {
      console.error('Error deleting job description:', error);
    }
  };

  const handleJDSaved = () => {
    fetchRoles(); // Refresh the roles to show updated data
  };

  const handleEditJDClick = (roleId: string, roleTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedRoleId(roleId);
    setSelectedRoleTitle(roleTitle);
    setEditJDDialogOpen(true);
  };

  const renderJobDescriptionCell = (role: RoleJD, index: number) => {
    // Check if role_description is an empty object or not generated yet
    if (!role.role_description || JSON.stringify(role.role_description) === '{}') {
      return (
        <span className="text-primary-light text-left">No JD found</span>
      );
    }
    
    if (role.role_description.text === 'No JD found') {
      return (
        <span className="text-primary-light text-left">No JD found</span>
      );
    }
    
    if (role.role_description.pdf_url || (role.role_description.text && role.role_description.text.trim() !== '')) {
      return (
        <button 
          className="text-primary hover:text-white text-left" 
          onClick={(e) => handleViewJDClick(role.id, e)}
        >
          View JD
        </button>
      );
    }
    
    return (
      <span className="text-primary-light text-left">No JD found</span>
    );
  };

  const renderActionsCell = (role: RoleJD, index: number) => {
    // Check if role_description is an empty object or not generated yet
    if (!role.role_description || JSON.stringify(role.role_description) === '{}') {
      return (
        <div className="flex items-center gap-2">
          <button 
            className="text-primary-light hover:text-primary" 
            onClick={(e) => handleProvideJDClick(role.id, role.role_title, e)}
            title="Provide Job Description"
          >
            <Upload size={16} />
          </button>
        </div>
      );
    }
    
    if (role.role_description.text === 'No JD found') {
      return (
        <div className="flex items-center gap-2">
          <button 
            className="text-primary-light hover:text-primary" 
            onClick={(e) => handleProvideJDClick(role.id, role.role_title, e)}
            title="Provide Job Description"
          >
            <Upload size={16} />
          </button>
        </div>
      );
    }
    
    if (role.role_description.pdf_url || (role.role_description.text && role.role_description.text.trim() !== '')) {
      return (
        <div className="flex items-center gap-2">
          <button 
            className="text-primary-light hover:text-primary" 
            onClick={(e) => handleEditJDClick(role.id, role.role_title, e)}
            title="Edit Job Description"
          >
            <Edit size={16} />
          </button>
          <button 
            className="text-red-500 hover:text-red-700" 
            onClick={(e) => handleDeleteJDClick(role.id, e)}
            title="Delete Job Description"
          >
            <Trash2 size={16} />
          </button>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2">
        <button 
          className="text-primary-light hover:text-primary" 
          onClick={(e) => handleProvideJDClick(role.id, role.role_title, e)}
          title="Provide Job Description"
        >
          <Upload size={16} />
        </button>
      </div>
    );
  };

  return (
    <>
      <div ref={tableContainerRef} className={cn("bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg shadow-lg shadow-primary/5 p-4 h-[450px] flex flex-col", "w-full", className, {
        "border-2 border-yellow-400": className?.includes("border-2 border-white")
      })}>
        <div className="flex flex-col items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Step 1: Select a table item</h3>
        </div>
        
        <div className="mb-3 bg-primary/5 backdrop-blur-lg border border-primary rounded-lg shadow-lg shadow-primary/5 py-2 px-4">
          <div className="flex items-center justify-between gap-2 flex-nowrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[8px] font-semibold text-primary whitespace-nowrap">{projectName}</span>
              <span className="text-primary text-[8px]">|</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="font-medium text-[8px] text-primary">Directors:</span>
                  <span className="text-white text-[8px]">{projectRequirements.directors_count}</span>
                </div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="font-medium text-[8px] text-primary">Managers:</span>
                  <span className="text-white text-[8px]">{projectRequirements.managers_count}</span>
                </div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="font-medium text-[8px] text-primary">Team Members:</span>
                  <span className="text-white text-[8px]">{projectRequirements.team_members_count}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 border-primary rounded">
          <div className="rounded-md border border-primary/20">
            <h3 className="text-[11px] text-primary-light text-center py-[8px]">Click the upload icon to add a JD, or select an item to generate a new JD</h3>
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/30 hover:bg-primary/30">
                    <TableHead className="w-[50px] sticky left-0 text-white text-left text-[11px] py-2">SN</TableHead>
                    <TableHead className="w-[250px] sticky left-[50px] text-white text-left text-[11px] py-2">Role Title</TableHead>
                    <TableHead className="w-[350px] sticky left-[300px] text-white text-left text-[11px] py-2">Job Description</TableHead>
                    <TableHead className="w-[100px] sticky left-[650px] text-white text-left text-[11px] py-2">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role, index) => (
                    <TableRow 
                      key={role.id} 
                      className={cn(
                        "transition-colors",
                        isLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                        "hover:bg-primary/20",
                        selectedRowIndex === index ? "bg-white text-black" : "bg-secondary-light/5"
                      )}
                      onClick={() => !isLoading && handleRowClick(index, role.id)}
                    >
                      <TableCell className={cn(
                        "text-left text-[11px] py-1 w-[50px] sticky left-0",
                        selectedRowIndex === index ? "text-black" : "text-white",
                        
                      )}>
                        {index + 1}
                      </TableCell>
                      <TableCell className={cn(
                        "text-left text-[11px] py-1 w-[250px] sticky left-[50px]",
                        selectedRowIndex === index ? "text-black" : "text-white",
                      )}>
                        {role.role_title}
                      </TableCell>
                      <TableCell className={cn(
                        "text-left text-[11px] py-1 w-[350px] sticky left-[300px] whitespace-normal",
                        selectedRowIndex === index ? "text-black" : "text-white",
                      )}>
                        {renderJobDescriptionCell(role, index)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-left text-[11px] py-1 w-[100px] sticky left-[650px] whitespace-normal",
                        selectedRowIndex === index ? "text-black" : "text-white",
                      )}>
                        {renderActionsCell(role, index)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <ViewJobDescriptionDialog
        open={viewJDDialogOpen}
        onOpenChange={setViewJDDialogOpen}
        roleId={selectedRoleId}
      />

      <ProvideJobDescriptionDialog
        open={provideJDDialogOpen}
        onOpenChange={setProvideJDDialogOpen}
        roleId={selectedRoleId}
        roleTitle={selectedRoleTitle}
        onSave={handleJDSaved}
      />

      <EditJobDescriptionDialog
        open={editJDDialogOpen}
        onOpenChange={setEditJDDialogOpen}
        roleId={selectedRoleId}
        roleTitle={selectedRoleTitle}
        onSave={handleJDSaved}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job Description</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job description? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
